/**
 * 获取通用数据
 * @description 
 *   1. 不传参数时，自动获取当前网址和标题
 *   2. 参数传false时，请求中不包含网址和标题参数
 *   3. 参数传对象时，使用对象中的网址和标题（一般只有非单页面应用需要传）
 * @param {Object|Boolean} [param] - 可选参数，控制请求行为
 * @param {String} [param.url] - 自定义来源网址，需符合URL格式
 * @param {String} [param.title] - 自定义来源标题
 * @returns {Promise} 返回包含响应数据的Promise对象，优先使用缓存，避免重复请求
 * @example
 * import fetchCommonData from 'xxx/fetchCommonData.js'
 * 或者 <script type="module" src="xxx/fetchCommonData.js"></script> 全局使用时用_fetchCommonData
 *
 * // 自动获取当前页面URL和标题
 * fetchCommonData().then(xxx);
 *
 * // 不传递URL和标题参数
 * fetchCommonData(false).then(xxx);
 *
 * // 使用自定义URL和标题
 * fetchCommonData({
 *   url: 'https://example.com',
 *   title: '示例标题'
 * }).then(xxx);
 */
const fetchCommonData = (() => {
  const cacheKey = '_common_data_wh';

  let isRequesting = false;
  let pendingPromise = null;

  // 发起请求并更新缓存
  const updateCache = (param) => {
    // 请求开始
    isRequesting = true;

    // 拼接URL参数，对title进行编码
    const url = new URL('https://wh-api.wenhua.com.cn/api/GeneralData/GetGeneralData');

    if (!(typeof param === 'boolean' && param === false)) {
      // 校验param.url是否是网址格式
      let sourceUrl = param && param.url && validDomain(param.url) ?
        param.url : (window.location.origin + window.location.pathname);

      let sourceTitle = param && param.title ? param.title : document.title;

      url.searchParams.append('sourceUrl', sourceUrl);
      url.searchParams.append('sourceTitle', sourceTitle);
    }

    // 存储当前请求的Promise
    pendingPromise = fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        // 更新缓存
        try {
          window.sessionStorage.setItem(cacheKey, JSON.stringify(data));
        } catch (error) {
          console.error('更新缓存失败:', error);
        }
        return data;
      })
      .catch(error => {
        console.error('请求失败:', error);
      })
      .finally(() => {
        // 重置
        isRequesting = false;
        pendingPromise = null;
      });

    return pendingPromise;
  };

  return (param) => {
    try {
      // 获取缓存
      const cachedData = window.sessionStorage.getItem(cacheKey);
      if (cachedData) {
        // 返回缓存数据
        const parsedData = JSON.parse(cachedData);
        // 返回缓存数据的Promise
        const cachePromise = Promise.resolve(parsedData);

        // 如果没有正在进行的请求
        if (!isRequesting) {
          updateCache(param); // 更新缓存
        }

        return cachePromise;
      }
    } catch (error) {
      console.error('获取缓存失败:', error);
    }

    // 无缓存 & 有正在进行的请求，返回 pendingPromise
    if (isRequesting && pendingPromise) {
      return pendingPromise;
    } else {  // 没有正在进行的请求，发起新请求
      return updateCache(param);
    }
  };
})();

// 校验URL格式
const validDomain = (str) => {
  const domainStartRegex = /^((https?:\/\/)?(www\.)?)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}/;
  return domainStartRegex.test(str);
};

if (typeof window !== 'undefined') {
  window._fetchCommonData = fetchCommonData; // 暴露全局变量
}

export default fetchCommonData;
